'use client';

import useSWR from 'swr';
import {
  ReactNode,
  createContext,
  useContext,
  useCallback,
  useState,
  useRef,
} from 'react';
import { Cart, CartLineEdge, CartLineUpdateInput } from '@/lib/shopify';
import { updateCart, removeFromCart } from '@/lib/cart.browser';
import {
  calculateTotalQuantity,
  updateCartLineQuantity,
} from '@/lib/cart/helpers';

interface CartContextValue {
  cart: Cart | null;
  isLoading: boolean;
  error: any;
  updateQuantity: (lineId: string, newQuantity: number) => Promise<void>;
  removeItem: (lineId: string) => Promise<void>;
  isMutating: boolean;
}

const CartContext = createContext({} as CartContextValue);

interface CartProviderProps {
  children: ReactNode;
  value?: CartContextValue;
}

export const CartContextProvider: React.FC<CartProviderProps> = ({
  children,
}) => {
  const fetcher = () =>
    fetch('/api/cart', { cache: 'no-cache' }).then((res) => res.json());

  const { data, isLoading, mutate, error } = useSWR<Cart>('/cart', fetcher);
  const [isMutating, setIsMutating] = useState(false);

  // AbortController references for canceling in-progress requests
  const updateQuantityAbortController = useRef<AbortController | null>(null);
  const removeItemAbortController = useRef<AbortController | null>(null);

  const updateQuantity = useCallback(
    async (lineId: string, newQuantity: number) => {
      if (!data?.id) return;

      const currentLine = data.lines.edges.find(
        (edge: any) => edge.node.id === lineId,
      )?.node;
      if (!currentLine) return;

      if (updateQuantityAbortController.current) {
        updateQuantityAbortController.current.abort();
      }

      updateQuantityAbortController.current = new AbortController();
      const signal = updateQuantityAbortController.current.signal;

      setIsMutating(true);

      const updatedCartLineEdges = data.lines.edges.map((edge: CartLineEdge) =>
        edge.node.id === lineId
          ? updateCartLineQuantity(edge, newQuantity)
          : edge,
      );

      const optimisticData = {
        ...data,
        lines: { edges: updatedCartLineEdges },
        totalQuantity: calculateTotalQuantity(updatedCartLineEdges),
      };

      try {
        const updateInput: CartLineUpdateInput = {
          id: lineId,
          quantity: newQuantity,
        };

        await mutate(
          updateCart([updateInput], {}, signal).then(async (result) => {
            if (!result.ok) {
              const errorResult = result as { ok: false; errorMessage: string };
              throw new Error(
                errorResult.errorMessage || 'Failed to update cart',
              );
            }

            // Return the cart data from the mutation response
            return (result as { ok: true; data: Cart }).data;
          }),
          {
            optimisticData,
            rollbackOnError: true,
            // Disable automatic revalidation since we're returning fresh data
            revalidate: false,
          },
        );
      } catch (error) {
        // Only log error if request wasn't aborted
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('[Cart] Failed to update cart quantity:', error);
          throw error;
        }
      } finally {
        setIsMutating(false);
        updateQuantityAbortController.current = null;
      }
    },
    [data, mutate],
  );

  const removeItem = useCallback(
    async (lineId: string) => {
      if (!data?.id) return;

      const currentLine = data.lines.edges.find(
        (edge: any) => edge.node.id === lineId,
      )?.node;
      if (!currentLine) return;

      if (removeItemAbortController.current) {
        removeItemAbortController.current.abort();
      }

      removeItemAbortController.current = new AbortController();
      const signal = removeItemAbortController.current.signal;

      setIsMutating(true);

      const filteredCartLineEdges = data.lines.edges.filter(
        (edge: any) => edge.node.id !== lineId,
      );

      const optimisticData = {
        ...data,
        lines: { edges: filteredCartLineEdges },
        totalQuantity: calculateTotalQuantity(filteredCartLineEdges),
      };

      try {
        await mutate(
          removeFromCart([lineId], {}, signal).then(async (result) => {
            if (!result.ok) {
              const errorResult = result as { ok: false; errorMessage: string };
              throw new Error(
                errorResult.errorMessage || 'Failed to remove item from cart',
              );
            }

            // Return the cart data from the mutation response
            return (result as { ok: true; data: Cart }).data;
          }),
          {
            optimisticData,
            rollbackOnError: true,
            // Disable automatic revalidation since we're returning fresh data
            revalidate: false,
          },
        );
      } catch (error) {
        // Only log error if request wasn't aborted
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Failed to remove cart item:', error);
          throw error;
        }
      } finally {
        setIsMutating(false);
        removeItemAbortController.current = null;
      }
    },
    [data, mutate],
  );

  const cartContextValue = {
    cart: data || null,
    isLoading,
    error,
    updateQuantity,
    removeItem,
    isMutating,
  };

  return (
    <CartContext.Provider value={cartContextValue}>
      {children}
    </CartContext.Provider>
  );
};

const useCartContext = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error('useCartContext must be used within a CartContextProvider');
  }

  return context;
};

export default useCartContext;
